<template>
  <div class="container-fluid sale-detail">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Cargando...</span>
      </div>
      <p class="mt-3 text-muted">Cargando detalle de venta...</p>
    </div>

    <div v-else class="sale-detail-content">
      <!-- View header -->
      <div class="view-header my-4">
        <nav class="view-header__breadcrumb" aria-label="breadcrumb">
          <router-link :to="{ name: 'Ventas' }">Ventas</router-link>
          <i class="bi bi-chevron-right view-header__breadcrumb-sep"></i>
          <span>Facturas</span>
          <template v-if="sale.pos && sale.number">
            <i class="bi bi-chevron-right view-header__breadcrumb-sep"></i>
            <span class="view-header__breadcrumb-current">
              {{ padLeft(sale.pos, 4) }}-{{ padLeft(sale.number, 8) }}
            </span>
          </template>
        </nav>

        <div class="view-header__main">
          <div class="view-header__info">
            <h1 class="view-header__title">Detalle de venta</h1>
            <p class="view-header__subtitle">Información detallada de la venta.</p>
          </div>

          <div class="view-header__actions">
            <template v-if="printTicket === 0">
              <DownloadDoc
                :pdfurl="pdfurl"
                :open="true"
                :filename="sale.client.name + sale.created_at + '.pdf'"
              >
                <button type="button" class="btn view-header__btn">
                  <i class="bi bi-printer"></i>
                  Imprimir
                </button>
              </DownloadDoc>
              <DownloadDoc
                :pdfurl="pdfurl"
                :open="false"
                :filename="sale.client.name + sale.created_at + '.pdf'"
              >
                <button type="button" class="btn view-header__btn view-header__btn--primary">
                  <i class="bi bi-download"></i>
                  Descargar PDF
                </button>
              </DownloadDoc>
            </template>
            <button
              v-else
              type="button"
              class="btn view-header__btn view-header__btn--primary"
              @click="print"
            >
              <i class="bi bi-printer"></i>
              Imprimir
            </button>
          </div>
        </div>
      </div>

      <!-- Invoice header card -->
      <div class="card invoice-header-card mb-4">
        <div class="invoice-header-main">
          <div class="invoice-type-block">
            <div class="invoice-type-icon">
              <i class="bi bi-file-earmark-text"></i>
            </div>
            <div class="invoice-type-info">
              <span class="invoice-type-label">Tipo de factura</span>
              <span class="invoice-type-value">
                {{ sale.type || 'Factura' }}<template v-if="sale.letter"> {{ sale.letter }}</template>
              </span>
              <span v-if="sale.letter && sale.letter !== 'P'" class="invoice-type-badge">
                Original
              </span>
            </div>
          </div>

          <div class="invoice-client-block">
            <div class="invoice-client-title">
              <span class="client-title-icon">
                <i class="bi bi-person"></i>
              </span>
              <span>Datos del cliente</span>
            </div>

            <div class="invoice-client-grid">
              <div class="client-field">
                <span class="field-label">Nombre / Razón social</span>
                <span class="field-value">{{ sale.client.name || '—' }}</span>
              </div>

              <div class="client-field">
                <span class="field-label">
                  <i class="bi bi-calendar3 field-icon"></i>
                  Fecha de emisión
                </span>
                <span class="field-value">{{ createdDateFormatted || '—' }}</span>
              </div>

              <div v-if="sale.number && sale.pos" class="client-field">
                <span class="field-label">
                  <i class="bi bi-file-text field-icon"></i>
                  N° de factura
                </span>
                <span class="field-value">
                  {{ padLeft(sale.pos, 4) }}-{{ padLeft(sale.number, 8) }}
                </span>
              </div>

              <div v-if="sale.client_cuit" class="client-field">
                <span class="field-label">
                  <i class="bi bi-card-text field-icon"></i>
                  CUIT / DNI
                </span>
                <span class="field-value">{{ sale.client_cuit }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasSecondaryInfo" class="invoice-header-secondary">
          <div v-if="sale.salesclerk != null" class="secondary-item">
            <span class="secondary-label">Vendedor</span>
            <span class="secondary-value">{{ sale.salesclerk }}</span>
          </div>
          <div v-if="sale.response && sale.response['CAE']" class="secondary-item">
            <span class="secondary-label">CAE</span>
            <span class="secondary-value secondary-value--mono">{{ sale.response['CAE'] }}</span>
            <span v-if="caeExpDateFormatted" class="secondary-sublabel">
              (vto. {{ caeExpDateFormatted }})
            </span>
          </div>
          <div v-if="sale.service_fromdate != null" class="secondary-item">
            <span class="secondary-label">Período</span>
            <span class="secondary-value">
              {{ serviceFromDateFormatted }} — {{ serviceToDateFormatted }}
              <template v-if="serviceExpDateFormatted">
                (venc. {{ serviceExpDateFormatted }})
              </template>
            </span>
          </div>
        </div>
      </div>

      <!-- Items + totals -->
      <div class="row">
        <!-- Items table -->
        <div class="col-md-8 mb-4 mb-md-0">
          <div class="card items-card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-box-seam"></i>
                </span>
                <h5 class="mb-0">Items</h5>
              </div>
              <div v-if="sale.sale_details && sale.sale_details.length > 0" class="items-summary">
                <span class="items-summary-item">
                  Total de ítems: <strong>{{ sale.sale_details.length }}</strong>
                </span>
                <span v-if="totalUnits > 0" class="items-summary-sep">|</span>
                <span v-if="totalUnits > 0" class="items-summary-item">
                  Total de unidades: <strong>{{ totalUnits }}</strong>
                </span>
              </div>
            </div>
            <div class="card-body p-4">
              <div class="table-responsive">
                <table class="table table-sm table-hover items-table mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th class="col-small">Cod</th>
                      <th>Item</th>
                      <th class="col-small text-center">Cantidad</th>
                      <th class="col-small text-right">Precio unit.</th>
                      <th class="col-small text-right">Desc.</th>
                      <th class="col-small text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(value, idx) in sale.sale_details"
                      :key="idx"
                    >
                      <td class="text-muted">{{ value.code }}</td>
                      <td>{{ value.description }}</td>
                      <td class="text-center">{{ value.quantity }}</td>
                      <td class="text-right">
                        {{ formatoDinamicoMoneda(value.price + value.discount) }}
                      </td>
                      <td class="text-right">
                        <span v-if="value.discount > 0" class="text-success">
                          {{ formatoDinamicoMoneda(value.discount) }}
                        </span>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td class="text-right font-weight-bold">
                        {{ formatoDinamicoMoneda(value.price * value.quantity) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Totals panel -->
        <div class="col-md-4">
          <div class="card totals-card mb-4">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-cash-coin"></i>
                </span>
                <h5 class="mb-0">Totales de la venta</h5>
              </div>
            </div>
            <div class="card-body">
              <div class="total-row">
                <span class="label">Subtotal</span>
                <span class="value">{{ formatoDinamicoMoneda(sale.subtotal) }}</span>
              </div>

              <div v-if="sale.discount > 0" class="total-row">
                <span class="label">Descuento</span>
                <span class="value text-success">
                  − {{ formatoDinamicoMoneda(sale.discount) }}
                </span>
              </div>

              <template v-if="sale.letter === 'A' || sale.letter === 'M'">
                <div class="total-row">
                  <span class="label">Neto gravado</span>
                  <span class="value">{{ formatoDinamicoMoneda(sale.nettaxed) }}</span>
                </div>
                <div
                  v-for="(tax, key) in sale.taxes"
                  :key="key"
                  class="total-row"
                >
                  <span class="label">IVA {{ key }}%</span>
                  <span class="value">{{ formatoDinamicoMoneda(tax) }}</span>
                </div>
              </template>

              <div class="totals-divider"></div>

              <div class="total-row total-final">
                <span class="label">Total</span>
                <span class="value">{{ formatoDinamicoMoneda(sale.total) }}</span>
              </div>

              <div v-if="hasPayments" class="payments-section">
                <div class="payments-title">Forma de pago</div>

                <div v-if="fundMovementAmount > 0" class="payment-row">
                  <span class="label">Efectivo</span>
                  <span class="value">
                    {{ formatoDinamicoMoneda(fundMovementAmount) }}
                  </span>
                </div>

                <div
                  v-for="coupon in couponsList"
                  :key="'c-' + coupon.sale_id"
                  class="payment-row"
                >
                  <span class="label">
                    {{ coupon.creditcard.name }}
                    <small class="text-muted d-block">N° {{ coupon.number }}</small>
                  </span>
                  <span class="value">{{ formatoDinamicoMoneda(coupon.amount) }}</span>
                </div>

                <div
                  v-for="tpcheck in tpchecksList"
                  :key="'t-' + tpcheck.sale_id"
                  class="payment-row"
                >
                  <span class="label">
                    Cheque
                    <small class="text-muted d-block">N° {{ tpcheck.number }}</small>
                    <span
                      class="badge"
                      :class="tpcheck.check_kind === 'echeq' ? 'badge-info' : 'badge-secondary'"
                    >
                      {{ checkKindLabels[tpcheck.check_kind] || 'Físico' }}
                    </span>
                  </span>
                  <span class="value">{{ formatoDinamicoMoneda(tpcheck.amount) }}</span>
                </div>

                <div v-if="totalca > 0" class="payment-row">
                  <span class="label">Cta. Cte.</span>
                  <span class="value">{{ formatoDinamicoMoneda(totalca) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="sale.notes" class="card notes-card">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-sticky"></i>
                </span>
                <h6 class="mb-0">Nota para el cliente</h6>
              </div>
            </div>
            <div class="card-body">
              <p class="mb-0 note-text">{{ sale.notes }}</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import DownloadDoc from "../../global/DownloadDoc";
import formatMoneyMixin from "../../../mixins/formatMoneyMixin";
import { mapState } from 'vuex';
import { CHECK_KIND_LABELS } from "../../../constants/checkKind";

export default {
  components: {
    DownloadDoc,
  },
  mixins: [formatMoneyMixin],
  props: ["saleId"],
  data() {
    return {
      loading: true,
      checkKindLabels: CHECK_KIND_LABELS,
      totalca: 0,
      pdfurl: process.env.VUE_APP_BASE_URL + '/api/sales/getURL/' + this.saleId,
      sale: {
        letter: null,
        pos: null,
        number: null,
        client_id: null,
        subtotal: null,
        discount: null,
        taxes: null,
        total: null,
        notes: '',
        sale_details: [],
        client: {
          name: null,
          clerk: { name: null },
        },
      },
      printTicket: null,
    };
  },
  computed: {
    ...mapState('authUser', {
      authUser: state => state.authUser,
    }),
    createdDateFormatted() {
      if (!this.sale.created_at) return '';
      return new Date(this.sale.created_at).toLocaleDateString('es-AR');
    },
    serviceFromDateFormatted() {
      if (!this.sale.service_fromdate) return '';
      return new Date(this.sale.service_fromdate).toLocaleDateString('es-AR');
    },
    serviceToDateFormatted() {
      if (!this.sale.service_todate) return '';
      return new Date(this.sale.service_todate).toLocaleDateString('es-AR');
    },
    serviceExpDateFormatted() {
      if (!this.sale.service_expdate) return '';
      return new Date(this.sale.service_expdate).toLocaleDateString('es-AR');
    },
    caeExpDateFormatted() {
      const raw = this.sale.response && this.sale.response['CAEFchVto'];
      if (!raw) return '';
      const str = String(raw);
      if (/^\d{8}$/.test(str)) {
        return `${str.slice(6, 8)}/${str.slice(4, 6)}/${str.slice(0, 4)}`;
      }
      const d = new Date(str);
      return isNaN(d) ? str : d.toLocaleDateString('es-AR');
    },
    hasSecondaryInfo() {
      return (
        this.sale.salesclerk != null ||
        (this.sale.response && this.sale.response['CAE']) ||
        this.sale.service_fromdate != null
      );
    },
    totalUnits() {
      if (!Array.isArray(this.sale.sale_details)) return 0;
      return this.sale.sale_details.reduce((sum, item) => {
        const q = Number(item.quantity) || 0;
        return sum + q;
      }, 0);
    },
    couponsList() {
      return Array.isArray(this.sale.coupons) ? this.sale.coupons : [];
    },
    tpchecksList() {
      return Array.isArray(this.sale.tpchecks) ? this.sale.tpchecks : [];
    },
    fundMovementAmount() {
      const fm = this.sale.fundMovement;
      if (!fm) return 0;
      if (typeof fm === 'number') return fm;
      return fm.amount || 0;
    },
    hasPayments() {
      return (
        this.fundMovementAmount > 0 ||
        this.couponsList.length > 0 ||
        this.tpchecksList.length > 0 ||
        this.totalca > 0
      );
    },
  },
  mounted() {
    this.getSaleDetail(this.saleId);
  },
  methods: {
    getSaleDetail(saleId) {
      this.loading = true;
      this.$http
        .get('/api/sales/getSale/' + saleId)
        .then(response => {
          if (response.data.success) {
            this.sale = response.data.data;
            this.getBranchofficesOptions();
          }
        })
        .catch(error => {
          console.error('Error al cargar detalle de venta:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    padLeft(number, length) {
      return number.toString().padStart(length, '0');
    },
    getBranchofficesOptions() {
      this.$http.get('/api/configs/branchoffice/getData').then(response => {
        const branchofficeSale = this.sale.bo_id;
        const branchoffices = response.data.data.branchoffices;
        const branchoffice = branchoffices.find(bo => bo.id === branchofficeSale);
        if (branchoffice && branchoffice.options.print_bill_ticket === 1) {
          this.printTicket = 1;
        } else {
          this.printTicket = 0;
        }
      });
    },
    print() {
      window.location.href = '/sales/ticket/' + this.saleId;
    },
  },
};
</script>
